import { QueryClient, QueryKey, UseQueryOptions } from "@tanstack/react-query";
import { GraphQLClient } from "graphql-request";

import { graphql } from "../../../generated/app-service-gql";
import { CurrentUserQuery } from "../../../generated/app-service-gql/graphql";
import { useProvidedAppToken } from "../../../providers/ApiTokenProvider";
import { useGraphQlQuery } from "../useGraphQlClient";
import { REACT_QUERY_OPTIONS_NEVER_REFETCH } from "../utils";

const CURRENT_USER_QUERY = graphql(`
  query currentUser {
    currentUser {
      guid
      id
      email
      firstName
      lastName
      jobTitle
      isVerified
      signupUtm {
        referrer
        url
        utmParams
        lastPageUrlBeforeFormSubmit
      }
      isAdmin
      isCompanyAdmin
      hasExportPermission
      hasAccountBriefingPermission
      sendAppActivityNotifications
      createdAt
      subscription
      seoParams {
        id
        dataType
      }
      activeDataTypes
      subscribedDataTypes
      dataTypeSubscriptions {
        dataTypeName
        disabled
      }
      company {
        id
        guid
        name
        demo
        analyticsTags {
          companyPaymentType
          companyIsIcp
          companyProfileType
          industry
          subIndustry
          partnerProgrammeSource
        }
        trialStart
        trialEnd
        hasInternationalDataAccess
        paymentType
        subscription
        createdAt
        allowCrmIntegration
        selfServe
        completedOnboarding
        unifiedContactsEnabled
        partnerProgramme {
          id
          name
          referralCode
          initialDataTypes
        }
        qualificationStates {
          defaultQualificationStateId
          defaultDisqualificationStateId
          qualificationStates {
            id
            label
            disqualifiesLead
            companyId
            rank
          }
        }
      }
      team {
        id
        name
        signalSettings {
          id
        }
        metadata {
          defaultSearchTopic
        }
      }
    }
  }
`);

export const CURRENT_USER_QUERY_KEY: QueryKey = ["currentUser"];

// Shared, client-independent options for the current-user query. The React hook reads these for
// cache key, bootstrap seeding and never-refetch behaviour; `useGraphQlQuery` supplies the actual
// `queryFn` from the context client.
const currentUserBaseOptions = {
  queryKey: CURRENT_USER_QUERY_KEY,
  // Feature specs run with no app-service; the Rails layout exposes a minimal current user as a
  // global (see shared/_test_current_user_stub) which seeds the cache so the fetch is skipped.
  // Undefined in dev/production, where app-service serves this query.
  initialData: window.__bootstrapCurrentUser,
  ...REACT_QUERY_OPTIONS_NEVER_REFETCH,
};

/**
 * Query options for fetching the current user, using a caller-supplied GraphQL client.
 *
 * Route loaders and `beforeLoad` guards run outside React, so they can't use the
 * `useCurrentUser` hook — they pass in the authenticated client from router context instead.
 *
 * The user is usually pre-loaded into the cache at app startup, so guards typically read it
 * without a network request.
 */
export const currentUserQueryOptions = (client: GraphQLClient) => ({
  ...currentUserBaseOptions,
  queryFn: () => client.request(CURRENT_USER_QUERY),
});

export type CurrentUserData = NonNullable<CurrentUserQuery["currentUser"]>;

/**
 * Synchronous current-user read for route loaders. `CurrentUserConfiguredProviders` seeds it before
 * the router mounts, so `undefined` means nobody is signed in.
 */
export function getCachedCurrentUser(queryClient: QueryClient): CurrentUserData | undefined {
  return (
    queryClient.getQueryData<CurrentUserQuery>(CURRENT_USER_QUERY_KEY)?.currentUser ?? undefined
  );
}

export function useCurrentUser(
  options?: UseQueryOptions<CurrentUserQuery, unknown, CurrentUserQuery, QueryKey>,
) {
  // The current user comes from app-service over authenticated GraphQL. Guests (public report /
  // lead URLs) have no app token, so the query stays disabled and the user resolves to undefined.
  const token = useProvidedAppToken();
  const { enabled: optionsEnabled, ...restOptions } = options ?? {};

  const { data, ...rest } = useGraphQlQuery(CURRENT_USER_QUERY_KEY, CURRENT_USER_QUERY, [], {
    // Reuse the shared options (initialData + never-refetch) so the bootstrap seeding stays in
    // one place. The client-bound `queryFn` is supplied by `useGraphQlQuery` from context.
    ...currentUserBaseOptions,
    ...restOptions,
    enabled: !!token && (optionsEnabled ?? true),
  });

  return {
    data: data?.currentUser,
    ...rest,
  };
}

/**
 * Returns the current user, throwing if it has loaded but is null. Drop-in replacement for the
 * old synchronous `assertCurrentUser()`: the bootstrap gate guarantees the query has resolved
 * before the app tree renders, so consumers get a non-null user without handling loading states.
 */
export function useAssertCurrentUser(): CurrentUserData {
  const { data: currentUser, isLoading } = useCurrentUser();
  if (!currentUser) {
    throw new Error(
      isLoading
        ? "useAssertCurrentUser called before the current user query resolved"
        : "Expected the current user to be defined.",
    );
  }
  return currentUser;
}
